import { CssBaseline, Theme, ThemeProvider } from '@mui/material'
import LoadingBackDrop from 'components/loading-backdrop'
import { USER_KEY } from 'constants/keys'
import { PropsWithChildren, createContext, useCallback, useContext, useEffect, useState } from 'react'
import api from 'services/api'
import { generateTheme } from 'styles/themes/default-theme'
import generateThemeResponse from 'utils/user/generate-theme-response'

export interface ICustomTheme {
  name: string
  setup: ITheme
  theme: Theme
  logo: ILogo
}

export interface IThemeResponse {
  primary: string
  secondary: string
  terciary: string
  baseLogo: string
  blackLogo: string
  whiteLogo: string
}

export interface ILogo {
  base: string
  white: string
  black: string
}

export interface ITheme {
  main: string
  light: string
  dark: string
  bg: {
    main: string
    light: string
    dark: string
  }
  info: {
    main: string
    light: string
    dark: string
  }
}

interface Iloading {
  isloading: boolean
  isInDelay: boolean
}

interface ICustomThemeContextData {
  theme: ICustomTheme
  setTheme: (theme: ICustomTheme) => void
  onChangeThema: (content: IThemeResponse) => void
  onResetThema: () => void
}

const defaultThemeSetup: ITheme = {
  main: '#3024E1',
  light: '#F0F0F0',
  dark: '#1A147C',
  bg: {
    main: '#31F7FF',
    light: '#97B8EF',
    dark: '#29B6DA',
  },
  info: {
    main: '#3024E1',
    light: '#362bdd',
    dark: '#231AA1',
  },
}

const CustomThemeContext = createContext<ICustomThemeContextData>({} as ICustomThemeContextData)

const CustomThemeProvider = ({ children }: PropsWithChildren) => {
  const [loading, setLoading] = useState<Iloading>({
    isloading: true,
    isInDelay: false,
  })

  // Tenta carregar o tema do localStorage imediatamente
  const getInitialTheme = (): ICustomTheme => {
    try {
      const storedUser = localStorage.getItem(USER_KEY)
      if (storedUser) {
        const user = JSON.parse(storedUser)
        if (!user.isSeller && user.theme != null) {
          const themeResponse = generateThemeResponse(user)
          const themeSetup = {
            main: themeResponse.primary,
            light: themeResponse.secondary,
            dark: themeResponse.terciary,
            bg: {
              main: themeResponse.secondary,
              light: themeResponse.secondary,
              dark: themeResponse.terciary,
            },
            info: {
              main: themeResponse.primary,
              light: themeResponse.primary,
              dark: themeResponse.primary,
            },
          }
          return {
            name: 'custom',
            setup: themeSetup,
            theme: generateTheme(themeSetup),
            logo: {
              base: themeResponse.baseLogo,
              white: themeResponse.whiteLogo,
              black: themeResponse.blackLogo,
            },
          }
        }
      }
    } catch (error) {
      console.error('Erro ao carregar tema do localStorage:', error)
    }

    return {
      name: 'default',
      setup: defaultThemeSetup,
      theme: generateTheme(defaultThemeSetup),
      logo: {
        base: '/assets/logo-conducco-all-white.svg',
        white: '/assets/logo-conducco-icon.svg',
        black: '',
      },
    }
  }

  const [theme, setTheme] = useState<ICustomTheme>(getInitialTheme())

  const handleMessage = (event: MessageEvent) => {
    if (event.data && typeof event.data.subdomain === 'string') {
      const subdomain = event.data.subdomain

      // if (subdomain == 'rita-rh') {
      //   setTheme({
      //     name: 'rita-rh',
      //     setup: ritaRHThemeSetup,
      //     theme: generateTheme(ritaRHThemeSetup),
      //   })
      // }
    }
  }

  const handleGetThemeForOrganization = useCallback(async () => {
    try {
      const response = await api.get(`/themes/validate`)

      return response.data
    } catch (error) {
      console.log(error)
    }
  }, [])

  const handleChangeTheme = useCallback(
    async (content: IThemeResponse) => {
      const theme = {
        main: content.primary,
        light: content.secondary,
        dark: content.terciary,
        bg: {
          main: content.secondary,
          light: content.secondary,
          dark: content.terciary,
        },
        info: {
          main: content.primary,
          light: content.primary,
          dark: content.primary,
        },
      }

      setTheme({
        name: 'custom',
        setup: theme,
        theme: generateTheme(theme),
        logo: {
          base: content.baseLogo,
          white: content.whiteLogo,
          black: content.blackLogo,
        },
      })
    },
    [setTheme]
  )

  const handleResetThema = () => {
    setTheme({
      name: 'default',
      setup: defaultThemeSetup,
      theme: generateTheme(defaultThemeSetup),
      logo: {
        base: '/assets/logo-conducco-all-white.svg',
        white: '/assets/logo-conducco-icon.svg',
        black: '',
      },
    })

    setLoading((prev) => ({ ...prev, isloading: false }))
  }

  useEffect(() => {
    let isMounted = true
    if (loading.isInDelay) {
      setTimeout(async () => {
        if (isMounted) {
          setLoading((prev) => ({ ...prev, isloading: false }))
        }
      }, 1000)
    }
  }, [loading.isInDelay])

  useEffect(() => {
    window.addEventListener('message', handleMessage)
    setLoading((prev) => ({ ...prev, isInDelay: true }))

    return () => window.removeEventListener('message', handleMessage)
  }, [])

  return (
    <CustomThemeContext.Provider
      value={{
        theme,
        setTheme,
        onChangeThema: handleChangeTheme,
        onResetThema: handleResetThema,
      }}
    >
      <ThemeProvider theme={theme.theme}>
        <CssBaseline />
        <LoadingBackDrop isloading={loading.isloading} />
        {!loading.isloading && children}
      </ThemeProvider>
    </CustomThemeContext.Provider>
  )
}

const useCustomTheme = () => useContext(CustomThemeContext)

export { CustomThemeProvider, useCustomTheme }
